import * as tvx from "./lib/tvx-plugin-module.min";
import { getInputEmail } from "./content/login";
import { IMAGES_PROXY, res_error_server, SERVER_ADDRESS } from "./static-res";
import { createSerialVideoTitle } from "./content/fictions/serial";
import { apiUser } from "./api/api-models";

const KEEP_RATIO_SUFFIX: string = "#screenify-keep-ratio";
const INTERACTION_REF_SUFFIX: string = "@" + window.location.href;

export const STORAGE_PREFIX_ID: string = "screenify:";
export const IS_LOGIN: string = "is_login";
export const ACTIVE_USER_DATA: string = "userData";

export function getShowingUserAccount(): string {
  const email = getInputEmail();
  const emailSubstring =
    email.length > 20 ? email.substring(0, 20) + "..." : email;
  return emailSubstring;
}

export function getUserData(): apiUser {
  const userDataString = localStorage.getItem(STORAGE_PREFIX_ID + ACTIVE_USER_DATA) || "";
  return base64ToObj(userDataString) as apiUser;
}

export function isLogin(): boolean {
  return localStorage.getItem(STORAGE_PREFIX_ID + IS_LOGIN) === "true";
}

export function isTokenValid(): boolean {
  try {
    // Проверяем, есть ли токен в localStorage
    const token = localStorage.getItem(STORAGE_PREFIX_ID + "accessToken");
    if (!token || token.trim() === '') {
      console.log("No access token found");
      return false;
    }
    
    // Проверяем, есть ли данные пользователя
    const userData = getUserData();
    if (!userData || !userData.id || !userData.email) {
      console.log("No valid user data found");
      return false;
    }
    
    // Проверяем, что пользователь помечен как залогиненный
    const isLoggedIn = localStorage.getItem(STORAGE_PREFIX_ID + IS_LOGIN);
    if (isLoggedIn !== "true") {
      console.log("User not marked as logged in");
      return false;
    }
    
    // Проверяем формат токена (должен быть JWT)
    if (!token.includes('.')) {
      console.log("Invalid token format");
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error checking token validity:", error);
    return false;
  }
}

// Асинхронная функция для проверки токена через API
export async function validateTokenWithAPI(): Promise<boolean> {
  try {
    const token = getAccessToken();
    if (!token) {
      console.log("No access token for API validation");
      return false;
    }
    
    console.log("Validating token with API...");
    console.log("Token:", token.substring(0, 20) + "...");
    console.log("API URL: https://api.screenify.shop/api/users/me");
    
    const response = await fetch("https://api.screenify.shop/api/users/me", {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      // Добавляем таймаут для избежания долгих ожиданий
      signal: AbortSignal.timeout(10000) // 10 секунд
    });
    
    console.log("API response status:", response.status);
    
    if (response.status === 401) {
      console.log("Token validation failed - 401 Unauthorized (account deleted or token invalid)");
      const errorText = await response.text();
      console.log("Error response:", errorText);
      return false;
    }
    
    if (response.status === 200) {
      try {
        const responseText = await response.text();
        console.log("API response text:", responseText);
        
        if (!responseText || responseText.trim() === '') {
          console.log("Empty response from API - treating as valid");
          return true;
        }
        
        const userData = JSON.parse(responseText);
        console.log("Token validation successful, user:", userData.email);
        return true;
      } catch (jsonError) {
        console.log("JSON parsing error, but status is 200 - treating as valid:", jsonError.message);
        return true;
      }
    }
    
    // Для других статусов (403, 500, etc.) считаем токен валидным
    // чтобы не выкидывать пользователя из-за проблем сервера
    console.log("API returned status:", response.status, "- treating as valid to avoid false positives");
    return true;
    
  } catch (error) {
    console.error("Error validating token with API:", error);
    
    // Если это ошибка сети или таймаут, не считаем это невалидным токеном
    if (error.name === 'AbortError' || error.name === 'TypeError') {
      console.log("Network error or timeout - treating as valid to avoid false positives");
      return true;
    }
    
    console.error("Error details:", error.message);
    return false;
  }
}

export function getAccessToken(): string | null {
  return localStorage.getItem(STORAGE_PREFIX_ID + "accessToken");
}

export function forceLogout(): void {
  console.log("=== FORCING USER LOGOUT ===");
  console.log("Reason: Invalid authentication detected");
  
  // Останавливаем таймер проверки аккаунта
  stopAccountValidationTimer();
  
  // Очищаем данные пользователя
  localStorage.setItem(STORAGE_PREFIX_ID + IS_LOGIN, "false");
  localStorage.setItem(STORAGE_PREFIX_ID + ACTIVE_USER_DATA, "");
  localStorage.setItem(STORAGE_PREFIX_ID + "accessToken", "");
  
  console.log("User data cleared from localStorage");
  
  // Показываем сообщение пользователю
  if (typeof tvx !== 'undefined' && tvx.InteractionPlugin) {
    console.log("Showing logout message to user");
    tvx.InteractionPlugin.error("Your session has expired. Please sign in again.");
    tvx.InteractionPlugin.executeAction("update:menu:sign-in", {
      label: "Sign In",
      icon: "login",
    });
    tvx.InteractionPlugin.executeAction("reload:content");
  } else {
    console.log("TVX InteractionPlugin not available");
  }
  
  console.log("=== LOGOUT COMPLETED ===");
}

// Глобальная функция проверки валидности аккаунта
export function validateAccount(): boolean {
  // Если пользователь не залогинен, разрешаем доступ к публичным функциям
  if (!isLogin()) {
    return true;
  }
  
  // Если пользователь залогинен, проверяем валидность токена
  if (!isTokenValid()) {
    console.log("Account validation failed - forcing logout");
    forceLogout();
    return false;
  }
  
  return true;
}

// Асинхронная глобальная функция проверки валидности аккаунта с API
export async function validateAccountAsync(): Promise<boolean> {
  // Если пользователь не залогинен, разрешаем доступ к публичным функциям
  if (!isLogin()) {
    return true;
  }
  
  // Если пользователь залогинен, проверяем валидность токена
  if (!isTokenValid()) {
    console.log("Account validation failed (local) - forcing logout");
    forceLogout();
    return false;
  }
  
  // Дополнительная проверка через API
  const isValid = await validateTokenWithAPI();
  if (!isValid) {
    console.log("Account validation failed (API) - forcing logout");
    forceLogout();
    return false;
  }
  
  return true;
}

// Функция проверки валидности аккаунта для защищенных действий
export function validateAccountForProtectedAction(): boolean {
  // Для защищенных действий всегда проверяем валидность
  if (!isLogin()) {
    console.log("User not logged in for protected action");
    return false;
  }
  
  if (!isTokenValid()) {
    console.log("Account validation failed for protected action - forcing logout");
    forceLogout();
    return false;
  }
  
  return true;
}

// Асинхронная функция проверки валидности аккаунта для защищенных действий с API
export async function validateAccountForProtectedActionAsync(): Promise<boolean> {
  // Для защищенных действий всегда проверяем валидность
  if (!isLogin()) {
    console.log("User not logged in for protected action");
    return false;
  }
  
  if (!isTokenValid()) {
    console.log("Account validation failed for protected action (local) - forcing logout");
    forceLogout();
    return false;
  }
  
  // Дополнительная проверка через API для критических действий
  const isValid = await validateTokenWithAPI();
  if (!isValid) {
    console.log("Account validation failed for protected action (API) - forcing logout");
    forceLogout();
    return false;
  }
  
  return true;
}

// Функция для периодической проверки валидности аккаунта
let accountValidationInterval: NodeJS.Timeout | null = null;

export function startAccountValidationTimer(): void {
  // Очищаем предыдущий таймер, если он существует
  if (accountValidationInterval) {
    clearInterval(accountValidationInterval);
  }
  
  // Проверяем валидность аккаунта каждые 15 секунд
  accountValidationInterval = setInterval(async () => {
    if (isLogin()) {
      console.log("=== PERIODIC ACCOUNT VALIDATION ===");
      
      // Сначала быстрая локальная проверка
      if (!isTokenValid()) {
        console.log("Periodic account validation failed (local) - forcing logout");
        forceLogout();
        return;
      }
      
      // Затем проверка через API с обработкой ошибок
      console.log("Checking token with API...");
      try {
        const isValid = await validateTokenWithAPI();
        if (!isValid) {
          console.log("Periodic account validation failed (API) - forcing logout");
          forceLogout();
        } else {
          console.log("Periodic account validation passed");
        }
      } catch (error) {
        console.log("API validation error (network issue), skipping logout:", error.message);
        // Не выкидываем пользователя при сетевых ошибках
      }
    }
  }, 15000); // 15 секунд
}

export function stopAccountValidationTimer(): void {
  if (accountValidationInterval) {
    clearInterval(accountValidationInterval);
    accountValidationInterval = null;
  }
}

// Функция для принудительной проверки аккаунта при каждом действии
export async function forceValidateAccountOnAction(): Promise<boolean> {
  console.log("=== FORCE VALIDATION ON ACTION ===");
  
  if (!isLogin()) {
    console.log("User not logged in, allowing action");
    return true;
  }
  
  console.log("User is logged in, checking token...");
  
  // Сначала локальная проверка
  if (!isTokenValid()) {
    console.log("Force validation failed (local) - forcing logout");
    forceLogout();
    return false;
  }
  
  // Для критических действий делаем проверку через API
  // Для обычных действий достаточно локальной проверки
  console.log("Force validation passed (local check only)");
  return true;
}

// Функция для тестирования системы проверки аккаунта
export async function testAccountValidation(): Promise<void> {
  console.log("=== TESTING ACCOUNT VALIDATION SYSTEM ===");
  
  console.log("1. Checking if user is logged in...");
  const isLoggedIn = isLogin();
  console.log("Is logged in:", isLoggedIn);
  
  if (!isLoggedIn) {
    console.log("User is not logged in, test complete");
    return;
  }
  
  console.log("2. Checking local token validity...");
  const isTokenValidLocal = isTokenValid();
  console.log("Token valid (local):", isTokenValidLocal);
  
  console.log("3. Checking token with API...");
  const isTokenValidAPI = await validateTokenWithAPI();
  console.log("Token valid (API):", isTokenValidAPI);
  
  console.log("4. Getting user data...");
  const userData = getUserData();
  console.log("User data:", userData);
  
  console.log("5. Getting access token...");
  const token = getAccessToken();
  console.log("Access token:", token ? token.substring(0, 20) + "..." : "None");
  
  console.log("=== TEST COMPLETE ===");
}

// Функция для ручного тестирования API проверки
export async function testAPIValidation(): Promise<void> {
  console.log("=== TESTING API VALIDATION ===");
  
  const token = getAccessToken();
  if (!token) {
    console.log("No access token found");
    return;
  }
  
  console.log("Testing API validation with current token...");
  const result = await validateTokenWithAPI();
  console.log("API validation result:", result);
  
  console.log("=== API TEST COMPLETE ===");
}

// Обертка для fetch с проверкой аутентификации
export async function authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
  // Проверяем, является ли эндпоинт публичным
  const isPublicEndpoint = url.includes('/auth/') || 
                          url.includes('/global') ||
                          url.includes('/mexico') ||
                          url.includes('/spain') ||
                          url.includes('/france') ||
                          url.includes('/brazil') ||
                          url.includes('/search') ||
                          url.includes('/charts/global') ||
                          url.includes('/charts/mexico') ||
                          url.includes('/charts/spain') ||
                          url.includes('/charts/france') ||
                          url.includes('/charts/brazil') ||
                          url.includes('/user-info/ip') ||
                          url.includes('api.ipify.org') ||
                          url.includes('analytics.screenify.fun');
  
  // Если это не публичный эндпоинт, проверяем аутентификацию
  if (!isPublicEndpoint) {
    if (!isLogin() || !isTokenValid()) {
      forceLogout();
      throw new Error('Authentication failed');
    }
    
    // Добавляем токен авторизации
    const token = getAccessToken();
    if (token) {
      options.headers = {
        ...options.headers,
        'Authorization': `Bearer ${token}`
      };
    }
  }
  
  const response = await fetch(url, options);
  
  // Проверяем ответ на 401 Unauthorized
  if (response.status === 401 && !isPublicEndpoint) {
    console.log("Received 401 Unauthorized, forcing logout");
    forceLogout();
    throw new Error('Unauthorized');
  }
  
  return response;
}

export function coloredText(color: string, text: string): string {
  return `{txt:${color}:${text}}`;
}

export function appendKeepRatioSuffix(url: string): string {
  return tvx.Tools.isFullStr(url) ? url + KEEP_RATIO_SUFFIX : null;
}

export function appendInteractionRefSuffix(url: string): string {
  return tvx.Tools.isFullStr(url) ? url + INTERACTION_REF_SUFFIX : null;
}

export function callCallback(
  data?: any,
  callback?: (data?: any) => void
): void {
  if (callback != null && typeof callback == "function") {
    callback(data != null ? data : null);
  }
}

export function objToBase64(obj: any): string {
  return tvx.Tools.base64Encode(tvx.Tools.serialize(obj));
}

export function base64ToObj(base64: string): any {
  return tvx.Tools.deserialize(tvx.Tools.base64DecodeUrl(base64));
}

export function createContentRequest(contentId: string): string {
  return appendInteractionRefSuffix("request:interaction:content:" + contentId);
}

export function createPanelRequest(panelId: string): string {
  return appendInteractionRefSuffix("request:interaction:panel:" + panelId);
}

export function stopLoadingShowError(
  emptyContent: tvx.AnyObject,
  callback: (data: any) => void
): void {
  callCallback(emptyContent, callback);
      tvx.InteractionPlugin.executeAction("info:" + res_error_server());
  tvx.InteractionPlugin.stopLoading();
}

let polyfixed: boolean = false;

export function polyfix(): void {
  //Note: This function is called polyfix (instead of polyfill), because it just fixes some TV-related parcel issues (instead of providing a fulfilled implementation)
  if (!polyfixed) {
    polyfixed = true;
    //Add Symbol fix
    if (typeof Symbol == "undefined") {
      window.Symbol = undefined;
    }
    //Add URL fix
    if (typeof URL == "undefined" || !URL.prototype.hasOwnProperty("href")) {
      //@ts-ignore
      window.URL = function (url: string) {
        this.href = url;
      };
    }
  }
}

export function getPosterUrl(imdbid: string, posterLink: string): string {
  return `${IMAGES_PROXY}posters/optimized?imdbid=${imdbid}&poster_link=${posterLink}`;
}

polyfix();
